import { afterNextRender, Component, DestroyRef, inject, Injector, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { TranslatePipe } from '@ngx-translate/core'
import { filter, map, of, switchMap, take, zip } from 'rxjs'
import { ActivatedRoute, Router } from '@angular/router'
import { AsyncPipe, Location } from '@angular/common'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { CallListComponent } from '../call-list/call-list.component'
import { Call, CallListResponse, CallsService, PastCallsType } from '../calls.service'
import { MatButtonToggleModule } from '@angular/material/button-toggle'
import { FormsModule } from '@angular/forms'
import { InfiniteData } from '@ngneat/query'
import { SpinnerComponent } from '../spinner/spinner.component'
import { UsersService } from '../users.service'
import { CalendarSyncService, CalendarSyncState, deriveCalendarSyncStateFromResult } from '../calendar-sync.service'
import { CalendarSyncStateComponent } from '../calendar-sync-state/calendar-sync-state.component'

@Component({
  selector: 'app-calls-dashboard',
  standalone: true,
  imports: [TranslatePipe, AsyncPipe, ToolbarComponent, CallListComponent, MatButtonToggleModule, FormsModule, SpinnerComponent, CalendarSyncStateComponent],
  templateUrl: './calls-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './calls-dashboard.component.scss',
})
export class CallsDashboardComponent implements OnInit {
  @ViewChild('pastCallList')
  pastCallList!: CallListComponent

  @ViewChild('upcomingCallList')
  upcomingCallList!: CallListComponent

  router = inject(Router)
  route = inject(ActivatedRoute)
  callsService = inject(CallsService)
  usersService = inject(UsersService)
  calendarSyncService = inject(CalendarSyncService)
  location = inject(Location)
  injector = inject(Injector)
  destroyRef = inject(DestroyRef)
  currentUser$ = this.usersService.currentUser$
  upcomingCallsQuery = this.callsService.getUpcomingCalls()
  // Users whose first synchronization is long done never need the status endpoint, so the poll is
  // skipped for everyone but the small number of users still waiting on it.
  calendarSyncState$ = this.currentUser$.pipe(
    switchMap((user) =>
      user === null || user.calendarSyncCompletedAt !== null
        ? of<CalendarSyncState>('IDLE')
        : this.calendarSyncService.getCalendarSyncStatus().pipe(map((result) => deriveCalendarSyncStateFromResult(result)))
    )
  )
  pastCallsType$ = this.route.data.pipe(map((data) => (data['mode'] as PastCallsType) ?? 'OWN_CALLS'))
  pastCalls$ = this.pastCallsType$.pipe(map((type) => this.callsService.getPastCalls(type, false)))

  ngOnInit() {
    this.scrollToRightCall()
  }

  private scrollToRightCall() {
    zip(
      this.upcomingCallsQuery.result$.pipe(
        filter((result) => result.isSuccess),
        take(1)
      ),
      this.pastCalls$.pipe(
        switchMap((query) => query.result$),
        filter((result) => result.isSuccess),
        take(1)
      )
    )
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        afterNextRender(
          {
            write: () => {
              const pastCallId = this.route.snapshot.queryParamMap.get('pastCallId')
              if (pastCallId && this.pastCallList) {
                this.pastCallList.scrollToCall(+pastCallId)
              }
              const upcomingCallId = this.route.snapshot.queryParamMap.get('upcomingCallId')
              if (upcomingCallId && this.upcomingCallList) {
                this.upcomingCallList.scrollToCall(+upcomingCallId)
              }
            },
          },
          { injector: this.injector }
        )
      })
  }

  getAllCalls(data: InfiniteData<CallListResponse, unknown>): Call[] {
    return data.pages.map((page) => page.calls).flat()
  }

  prefetchCall(call: Call) {
    this.callsService.prefetchCall(call.id)
  }

  setPastCallQueryParameter(callId: number) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { pastCallId: callId },
      queryParamsHandling: 'merge',
    })
  }

  setUpcomingCallQueryParameter(callId: number) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { upcomingCallId: callId },
      queryParamsHandling: 'merge',
    })
  }
}
